import { Link } from "react-router-dom";
import { ArrowUpRight, Wind, TerminalSquare, Waypoints, Building2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const GROUPS = [
  {
    icon: Wind,
    title: "Ecossistema Microsoft",
    desc: "Plataformas de servidor, identidade e produtividade que sustentam a operação corporativa.",
    items: [
      "Windows Server 2022/2025",
      "Active Directory Domain Services",
      "Hyper-V Failover Cluster",
      "Microsoft Azure",
      "Entra ID (Azure AD)",
      "Microsoft 365 Enterprise",
      "Exchange Online",
      "Automação com PowerShell",
    ],
    testId: "tech-group-microsoft",
  },
  {
    icon: TerminalSquare,
    title: "Ambientes Linux e Open Source",
    desc: "Servidores, serviços web e automação com ferramentas consolidadas da comunidade.",
    items: [
      "Ubuntu Server Enterprise",
      "Debian GNU/Linux",
      "Red Hat Enterprise Linux",
      "NGINX / Apache HTTP Server",
      "Containers Docker",
      "Infraestrutura PostgreSQL / MySQL",
      "Monitoramento Zabbix",
      "Scripting Bash / Python",
    ],
    testId: "tech-group-linux",
  },
  {
    icon: Waypoints,
    title: "Redes e Segurança Perimetral",
    desc: "Segmentação, controle de tráfego e conectividade segura entre matrizes, filiais e trabalho remoto.",
    items: [
      "VLANs 802.1Q e switching Layer 3",
      "Firewalls Fortinet FortiGate",
      "Mikrotik RouterOS",
      "pfSense / OPNsense",
      "VPN IPsec e OpenVPN S2S",
      "Wi-Fi corporativo enterprise",
      "Roteamento SD-WAN",
      "QoS e traffic shaping",
    ],
    testId: "tech-group-networking",
  },
  {
    icon: Building2,
    title: "Infraestrutura e Data Center",
    desc: "Base física e lógica do ambiente: storage, energia, cabeamento e proteção de dados.",
    items: [
      "Veeam Backup & Replication",
      "Storage SAN / NAS (iSCSI e NFS)",
      "Servidores Dell PowerEdge",
      "Servidores HPE ProLiant",
      "Nobreaks / UPS senoidais",
      "Cabeamento estruturado Cat6A / fibra",
      "Monitoramento de data center",
      "Estratégia de backup 3-2-1-1-0",
    ],
    testId: "tech-group-infrastructure",
  },
];

export default function Technologies() {
  return (
    <div>
      <PageHead
        title="Tecnologias & Ecossistema de Infraestrutura | IPRS Soluções em TI"
        description="Plataformas e padrões utilizados em nossos projetos: Microsoft, Linux, redes e segurança, infraestrutura e data center."
        path="/tecnologias"
      />

      <section className="relative pt-28 lg:pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Tecnologias</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-graphite leading-[1.15] max-w-3xl">
              Plataformas e padrões industriais homologados
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base lg:text-lg text-steel leading-relaxed max-w-2xl">
              Selecionamos tecnologias consolidadas de mercado para construir ambientes
              estáveis, seguros e fáceis de evoluir.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-5">
          {GROUPS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.06} className="h-full">
              <div
                data-testid={g.testId}
                className="h-full flex flex-col bg-white border border-line rounded-xl p-8 hover:border-techblue/50 transition-colors duration-200"
              >
                <div className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-lg bg-pale flex items-center justify-center text-techblue shrink-0">
                    <g.icon className="w-5 h-5" />
                  </span>
                  <h2 className="text-lg lg:text-xl font-semibold text-graphite">{g.title}</h2>
                </div>
                <p className="mt-3 text-sm text-steel leading-relaxed">{g.desc}</p>
                <ul className="mt-6 flex flex-wrap gap-2 flex-1 content-start">
                  {g.items.map((item) => (
                    <li
                      key={item}
                      className="font-mono text-xs text-graphite bg-mist border border-line rounded-md px-3 py-1.5"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="container-x mt-10">
          <Reveal>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-pale border border-line rounded-xl px-6 sm:px-8 py-6">
              <p className="text-sm text-graphite leading-relaxed flex-1">
                Sua operação utiliza outras plataformas? Avaliamos o cenário atual e
                recomendamos o caminho mais seguro para modernizar.
              </p>
              <Link
                to="/contato"
                data-testid="tech-contact-link"
                className="inline-flex items-center gap-1.5 bg-techblue hover:bg-techblue-hover text-white text-sm font-semibold px-6 py-3 rounded-md transition-colors duration-200 shrink-0"
              >
                Fale com um especialista
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
